import React from 'react';
import Modal from '../ui/Modal';
import { Badge } from '../ui/badge';
import { Clock, User } from 'lucide-react';
import { JobContactEntry, JobContactEntryHistoryDto } from '../../types';

interface TimelineEntryHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  entry: JobContactEntry | null;
  history: JobContactEntryHistoryDto[] | null | undefined;
}

const TimelineEntryHistoryModal: React.FC<TimelineEntryHistoryModalProps> = ({
  isOpen,
  onClose,
  entry,
  history,
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit History" maxWidth="3xl">
      <div className="space-y-4">
        {entry && (
          <div className="p-3 rounded-md bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold bg-accent-color text-white !bg-accent-color">
                {entry.entryTypeName}
              </span>
              <span className="text-xs text-gray-500">Entry ID #{entry.id}</span>
            </div>
            <div className="text-sm text-gray-700 dark:text-gray-300 line-clamp-3">{entry.message}</div>
          </div>
        )}

        {!history || history.length === 0 ? (
          <div className="text-center text-gray-500 dark:text-gray-400 py-8">No edit history available</div>
        ) : (
          <div className="space-y-4">
            {history.map((h) => (
              <div key={h.id} className="relative pl-6">
                <div className="absolute left-0 top-1.5 h-3 w-3 rounded-full bg-accent-color"></div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                      <span className="flex items-center gap-1"><User className="h-4 w-4" /> {h.editedByName || h.editedBy}</span>
                      <span className="text-gray-400">•</span>
                      <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {new Date(h.editedDate).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="text-sm">
                    <div className="text-gray-500 dark:text-gray-400">Reason:</div>
                    <div className="mt-1 p-2 rounded bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                      {h.editReason}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Before</div>
                      <div className="p-2 rounded bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {h.previousMessage}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">After</div>
                      <div className="p-2 rounded bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-sm text-green-800 dark:text-green-300 whitespace-pre-wrap">
                        {h.newMessage}
                      </div>
                    </div>
                  </div>
                  {(h.previousMetadata || h.newMetadata) && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">Metadata was modified</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TimelineEntryHistoryModal;


